import { useState } from 'react'
import AdminLayout from '@/layouts/AdminLayout'
import toast from 'react-hot-toast'

function AdminHelpPage() {
  const [activeCategory, setActiveCategory] = useState('getting-started')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFaq, setExpandedFaq] = useState(null)

  const categories = [
    {
      id: 'getting-started',
      name: 'Démarrage',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-700'
    },
    {
      id: 'users',
      name: 'Gestion des utilisateurs',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: 'from-green-500 to-green-700'
    },
    {
      id: 'content',
      name: 'Publications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      color: 'from-purple-500 to-purple-700'
    },
    {
      id: 'settings',
      name: 'Paramètres',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'from-orange-500 to-orange-700'
    },
    {
      id: 'troubleshooting',
      name: 'Dépannage',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      color: 'from-red-500 to-red-700'
    }
  ]

  const helpContent = {
    'getting-started': [
      {
        title: 'Premiers pas avec KUDHI Admin',
        description: 'Découvrez comment naviguer dans le panneau d\'administration et accéder aux fonctionnalités principales.',
        steps: [
          'Connectez-vous avec vos identifiants administrateur',
          'Accédez au tableau de bord pour voir les statistiques',
          'Explorez le menu latéral pour découvrir toutes les fonctionnalités',
          'Personnalisez votre profil dans "Mon profil"'
        ]
      },
      {
        title: 'Navigation du tableau de bord',
        description: 'Le tableau de bord vous donne un aperçu complet de l\'activité de la plateforme.',
        steps: [
          'Consultez les statistiques en temps réel',
          'Visualisez les graphiques de performance',
          'Accédez rapidement aux actions importantes',
          'Surveillez les notifications importantes'
        ]
      }
    ],
    'users': [
      {
        title: 'Créer un nouvel utilisateur',
        description: 'Ajoutez de nouveaux utilisateurs ou administrateurs à la plateforme.',
        steps: [
          'Allez dans "Gestionnaire des utilisateurs"',
          'Cliquez sur "Ajouter un utilisateur"',
          'Remplissez les informations requises (nom, email, rôle)',
          'Validez pour créer le compte'
        ]
      },
      {
        title: 'Gérer les rôles et permissions',
        description: 'Attribuez et modifiez les rôles des utilisateurs.',
        steps: [
          'Sélectionnez un utilisateur dans la liste',
          'Cliquez sur "Modifier le rôle"',
          'Choisissez parmi : Super Admin, Admin, Superviseur, etc.',
          'Sauvegardez les modifications'
        ]
      },
      {
        title: 'Archiver un utilisateur',
        description: 'Désactivez temporairement un compte utilisateur.',
        steps: [
          'Trouvez l\'utilisateur dans la liste',
          'Cliquez sur l\'icône d\'archive',
          'Confirmez l\'archivage',
          'L\'utilisateur ne pourra plus se connecter'
        ]
      }
    ],
    'content': [
      {
        title: 'Publier du contenu',
        description: 'Créez et publiez des articles, vidéos ou bannières.',
        steps: [
          'Accédez à la section "Publications"',
          'Cliquez sur "Nouvelle publication"',
          'Sélectionnez le type (Article, Vidéo, Bannière)',
          'Remplissez les détails et ajoutez le média',
          'Choisissez la durée et validez'
        ]
      },
      {
        title: 'Modérer les publications',
        description: 'Gérez les publications en attente de validation.',
        steps: [
          'Consultez les publications en attente',
          'Examinez le contenu et les médias',
          'Approuvez ou rejetez selon les critères',
          'Ajoutez des commentaires si nécessaire'
        ]
      }
    ],
    'settings': [
      {
        title: 'Configurer les tarifs',
        description: 'Définissez les prix des publications par type et durée.',
        steps: [
          'Allez dans "Paramètres > Tarifs"',
          'Modifiez les prix pour chaque type de contenu',
          'Ajustez les tarifs selon la durée',
          'Sauvegardez vos modifications'
        ]
      },
      {
        title: 'Gérer les notifications',
        description: 'Configurez vos préférences de notifications.',
        steps: [
          'Accédez à "Paramètres > Notifications"',
          'Activez/désactivez les notifications système',
          'Configurez les notifications par email',
          'Enregistrez vos préférences'
        ]
      }
    ],
    'troubleshooting': [
      {
        title: 'Problèmes de connexion',
        description: 'Que faire si vous ne pouvez pas vous connecter.',
        steps: [
          'Vérifiez vos identifiants (email et mot de passe)',
          'Assurez-vous d\'avoir les permissions nécessaires',
          'Videz le cache de votre navigateur',
          'Contactez le Super Admin si le problème persiste'
        ]
      },
      {
        title: 'Erreurs lors de l\'upload',
        description: 'Résolvez les problèmes d\'upload de fichiers.',
        steps: [
          'Vérifiez la taille du fichier (max 5MB pour les images)',
          'Utilisez des formats supportés (JPG, PNG, MP4)',
          'Vérifiez votre connexion internet',
          'Réessayez avec un fichier différent'
        ]
      }
    ]
  }

  const faqs = [
    {
      question: 'Comment réinitialiser mon mot de passe ?',
      answer: 'Vous pouvez réinitialiser votre mot de passe en allant dans "Paramètres > Sécurité" et en cliquant sur "Changer le mot de passe". Vous devrez entrer votre mot de passe actuel puis le nouveau.'
    },
    {
      question: 'Quelle est la différence entre archiver et supprimer ?',
      answer: 'Archiver un utilisateur ou une publication le désactive temporairement sans supprimer les données. Vous pouvez le restaurer à tout moment. La suppression est permanente et irréversible.'
    },
    {
      question: 'Comment voir l\'historique des activités ?',
      answer: 'Allez dans la section "Activités" pour voir un journal complet de toutes les actions effectuées sur la plateforme, avec les détails et la date de chaque action.'
    },
    {
      question: 'Puis-je modifier les tarifs après publication ?',
      answer: 'Oui, vous pouvez modifier les tarifs à tout moment dans "Paramètres > Tarifs". Les nouveaux tarifs s\'appliqueront aux futures publications, pas aux publications existantes.'
    },
    {
      question: 'Comment contacter le support technique ?',
      answer: 'Vous pouvez contacter le support via email à support@kudhi.net ou par téléphone au +225 XX XX XX XX XX. Le support est disponible du lundi au vendredi de 9h à 18h.'
    }
  ]

  const handleContactSupport = () => {
    toast.success('Formulaire de contact en cours de développement')
  }

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <AdminLayout>
      <div className="w-full mx-auto mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Support Card */}
            <div className="sticky top-28 card-ligdi bg-gradient-to-br from-[#FFD700] to-[#F5C800] text-[#002A4E]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-md flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg">Besoin d aide ?</h3>
                  <p className="text-sm opacity-90">Contactez le support</p>
                </div>
              </div>
              <p className="text-sm mb-4 opacity-90">
                Notre équipe est disponible pour vous aider avec toute question ou problème.
              </p>
              <button 
                onClick={handleContactSupport}
                className="w-full px-4 py-3 bg-white text-[#B8860B] rounded-md font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 border border-slate-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contacter le support
              </button>
              <div className="mt-4 pt-4 border-t border-white/20 space-y-2">
                <a href="mailto:support@kudhi.net" className="flex items-center gap-2 text-sm hover:underline">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  support@kudhi.net
                </a>
                <a href="tel:+22500000000" className="flex items-center gap-2 text-sm hover:underline">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +225 XX XX XX XX XX
                </a>
              </div>
            </div>

            {/* Categories */}
            <div className="card-ligdi">
              <h3 className="text-lg font-semibold text-[#002A4E] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#B8860B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Catégories
              </h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                      activeCategory === category.id
                        ? 'bg-[#FFD700]/10 text-[#B8860B] font-semibold '
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {category.icon}
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card-ligdi">
              <h3 className="text-lg font-semibold text-[#002A4E] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#B8860B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Le saviez-vous ?
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                  <p className="text-sm text-blue-800">
                    💡 Utilisez les raccourcis clavier pour naviguer plus rapidement dans l interface
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-md border border-green-100">
                  <p className="text-sm text-green-800">
                    ✨ Vous pouvez exporter les données en PDF ou Excel depuis le tableau de bord
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-md border border-purple-100">
                  <p className="text-sm text-purple-800">
                    🔔 Activez les notifications par email pour rester informé des activités importantes
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search */}
            <div className="card-ligdi">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher dans l aide..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>
            </div>

            {/* Help Content */}
            <div className="card-ligdi">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                <div className={`w-12 h-12 bg-gradient-to-br ${categories.find(c => c.id === activeCategory)?.color} rounded-md flex items-center justify-center text-white `}>
                  {categories.find(c => c.id === activeCategory)?.icon}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E]">
                    {categories.find(c => c.id === activeCategory)?.name}
                  </h2>
                  <p className="text-sm text-gray-600">Guides et tutoriels détaillés</p>
                </div>
              </div>

              <div className="space-y-6">
                {helpContent[activeCategory]?.map((item, index) => (
                  <div key={index} className="p-5 bg-gray-50 rounded-md border border-gray-200 transition-shadow">
                    <h3 className="text-xl font-semibold text-[#002A4E] mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#B8860B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {item.title}
                    </h3>
                    <p className="text-gray-700 mb-4">{item.description}</p>
                    <div className="space-y-2">
                      {item.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-[#FFD700] text-[#002A4E] rounded-full flex items-center justify-center text-sm font-bold">
                            {stepIndex + 1}
                          </div>
                          <p className="text-gray-700 flex-1 pt-0.5">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="card-ligdi">
              <h2 className="text-2xl font-bold text-[#002A4E] mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-[#B8860B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Questions Fréquentes
              </h2>
              <div className="space-y-3">
                {filteredFaqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                    >
                      <span className="font-semibold text-[#002A4E] flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#B8860B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {faq.question}
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          expandedFaq === index ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedFaq === index && (
                      <div className="px-5 py-4 bg-white border-t border-gray-200">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
                {filteredFaqs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Aucune question trouvée pour {searchQuery}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminHelpPage
